import * as React from 'react'
import { Alert, Button, Card } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { Status } from 'api/common/statuses'
import { getWithdrawalInfoAPI, submitWithdrawalRequestAPI, WithdrawalBalance, ExpiringReward, BankAccount, PositionRewardStatus } from 'api/withdrawal'
import Footer from 'components/Footer'
import Header from 'components/Header'
import RewardStatusBadges from 'components/RewardStatusBadges'
import WithdrawalConfirmModal from 'components/WithdrawalConfirmModal'
import loginData from 'utilities/loginData'
import { numberFormat, dateFormatter } from 'utilities/typeUtility'
import { WithdrawalRequestConstants } from 'constants/withdrawalRequest'

/**
 * 出金申請画面
 */
const ShowWithdrawal: React.FC = () => {
  const navigate = useNavigate()
  const [balance, setBalance] = React.useState<WithdrawalBalance | null>(null)
  const [expiringRewards, setExpiringRewards] = React.useState<ExpiringReward[]>([])
  const [bankAccount, setBankAccount] = React.useState<BankAccount | null>(null)
  const [showModal, setShowModal] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [positions, setPositions] = React.useState<PositionRewardStatus[]>([])
  const [showSuccessAlert, setShowSuccessAlert] = React.useState(false)
  const successAlertTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  const fetchRewards = () => {
    getWithdrawalInfoAPI((response, status) => {
      if (status === Status.Success && response) {
        setBalance(response.balance)
        setExpiringRewards(response.expiringRewards)
        setBankAccount(response.bankAccount)
        setPositions(response.positions || [])
      }
    })
  }

  React.useEffect(() => {
    if (!loginData.isLoggedIn()) {
      navigate('/')
      return
    }

    fetchRewards()

    if (successAlertTimeoutRef.current) {
      clearTimeout(successAlertTimeoutRef.current)
      successAlertTimeoutRef.current = null
    }
  }, [navigate])

  const handleWithdrawalRequest = () => {
    setShowModal(true)
  }

  const handleConfirmWithdrawal = () => {
    if (!balance) return

    setIsLoading(true)
    submitWithdrawalRequestAPI(balance.availableAmount, (status) => {
      setIsLoading(false)
      if (status === Status.Success) {
        setShowModal(false)
        setShowSuccessAlert(true)

        successAlertTimeoutRef.current = setTimeout(() => {
          setShowSuccessAlert(false)
          successAlertTimeoutRef.current = null
        }, 3000)

        fetchRewards()
      }
    })
  }

  const handleHistoryClick = () => {
    navigate('/withdrawHistories')
  }

  const handleBankAccountEdit = () => {
    navigate('/connectorEdit')
  }

  return (
    <>
      <Header title="アフィリエイトボーナス出金申請" />
      {/* Success Alert */}
      {showSuccessAlert && (
        <Alert variant="success" className="text-center">
          <div className="d-flex align-items-center justify-content-center">
            <span style={{ fontSize: '18px', marginRight: '8px' }}>✅</span>
            <strong>出金申請が正常に受け付けられました</strong>
          </div>
        </Alert>
      )}
      {/* Status Badges */}
      <div style={{ textAlign: 'center', marginTop: showSuccessAlert ? '-5px' : '-25px' }}>
        <RewardStatusBadges positions={positions} />
      </div>
      <main>
        {/* Balance Overview Section */}
        <section className="mx-4 my-4">
          {/* Reward Target Balance */}
          <Card className="mb-3">
            <Card.Body className="d-flex justify-content-between align-items-center">
              <div style={{ color: 'black', fontSize: '14px', fontWeight: 400 }}>報酬対象残高</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e1e1e' }}>{balance ? numberFormat(balance.totalReceivedAmount) : '0'}円</div>
            </Card.Body>
          </Card>

          {/* Withdrawable Amount */}
          <Card>
            <Card.Body className="d-flex justify-content-between align-items-center">
              <div style={{ color: 'black', fontSize: '14px', fontWeight: 400 }}>出金申請可能額</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e1e1e' }}>{numberFormat(balance ? balance.availableAmount : 0)}円</div>
            </Card.Body>
          </Card>
        </section>

        {/* Expiry Warning Section */}
        {expiringRewards.length > 0 && (
          <section className="mx-4 mb-4">
            <div
              style={{
                background: '#fff9e6',
                border: '1px solid #ffd666',
                borderRadius: '5px',
                padding: '16px',
              }}
            >
              <div
                style={{
                  color: '#d46b08',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                ⚠️ 有効期限間近の報酬
              </div>
              {expiringRewards.map((reward, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: index < expiringRewards.length - 1 ? '1px solid #ffd666' : 'none',
                  }}
                >
                  <div style={{ color: 'black', fontSize: '13px' }}>有効期限: {dateFormatter(reward.expiryDate, 'yyyy/MM/dd')}</div>
                  <div style={{ color: '#d46b08', fontWeight: 'bold', fontSize: '16px' }}>{numberFormat(reward.amount)}円</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Warning Messages */}
        <section className="mx-4 mb-4">
          <div
            style={{
              background: '#fff5f5',
              border: '1px solid #ffcccc',
              borderRadius: '5px',
              padding: '16px',
            }}
          >
            <div
              style={{
                color: '#b42318',
                fontWeight: 'bold',
                fontSize: '14px',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              ❗ 重要なお知らせ
            </div>
            <div style={{ color: '#b42318', fontSize: '13px', lineHeight: '20px' }}>
              ※ 別途振込手数料がかかります。振込手数料はお客様負担となります。
              <br />※ 報酬の有効期限は{WithdrawalRequestConstants.REWARD_VALIDITY_YEARS}年間です。期限が切れると失効となりますので、期限内に出金申請を行ってください。
            </div>
          </div>
        </section>

        {/* Information Section */}
        <section className="mx-4 mb-4">
          <section className="info" style={{ position: 'relative' }}>
            {/* Information Icon */}
            <svg
              width="23"
              height="32"
              fill="#1e1e1e"
              style={{
                position: 'absolute',
                top: '0',
                left: '0',
                aspectRatio: '0.71',
                objectFit: 'contain',
                objectPosition: 'center',
                width: '23px',
                fill: '#fff',
                zIndex: 10,
              }}
            >
              <circle cx="11.5" cy="11.5" r="10" stroke="#1e1e1e" strokeWidth="1.5" fill="white" />
              <text x="11.5" y="16" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#1e1e1e">
                i
              </text>
            </svg>

            <div style={{ paddingLeft: '25px' }}>
              <strong>出金申請について</strong>
              <br />
              <div className="mt-1">
                • 最低出金額は{WithdrawalRequestConstants.MIN_WITHDRAWAL_AMOUNT}円以上となります
                <br />• 申請後、{WithdrawalRequestConstants.MIN_DAYS_TO_PROCESS}営業日以内に指定口座へ振込いたします
              </div>
            </div>
          </section>
        </section>

        {/* Withdrawal Request Button Section */}
        <section className="bg-white px-4 py-5">
          <Button
            variant="success"
            className="w-100 py-4 mb-3"
            onClick={handleWithdrawalRequest}
            disabled={!balance || balance.availableAmount < WithdrawalRequestConstants.MIN_WITHDRAWAL_AMOUNT || isLoading}
          >
            出金申請を行う
          </Button>

          <Button variant="outline-primary" className="w-100 py-3" onClick={handleHistoryClick}>
            出金履歴を確認
          </Button>

          {/* Additional Actions */}
          <div className="mt-4">
            <hr className="my-4" />
            <Button variant="primary" className="w-100 py-3 mb-3" onClick={handleBankAccountEdit}>
              振込先口座を変更
            </Button>
          </div>
        </section>
      </main>
      <Footer />

      {/* Withdrawal Confirmation Modal */}
      {balance && (
        <WithdrawalConfirmModal
          show={showModal}
          onHide={() => setShowModal(false)}
          onConfirm={handleConfirmWithdrawal}
          amount={balance.availableAmount}
          bankAccount={bankAccount}
          isLoading={isLoading}
        />
      )}
    </>
  )
}

export default ShowWithdrawal
